/** Containment for markdown render failures: one bad message degrades to its raw source instead of taking the
 *  page down. Why raw HTML stays off and why this never auto-resets: docs/adrs/0013. */

import { ErrorBoundary } from "@sentry/react"
import { Box, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import type { ReactNode } from "react"

// Styled like the fenced-code card (same border/background variables, monospace): shown in the normal body
// font, the raw markdown would look like the model literally typed "> [!NOTE]" rather than like a rendering fault.
const FALLBACK_SX = {
  marginBlock: "1rem",
  padding: "0.75rem 1rem",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "var(--app-overlay-border)",
  borderRadius: "6px",
  backgroundColor: "var(--app-bg-muted)",
} satisfies SxProps<Theme>
const NOTICE_SX = { display: "block", mb: 0.75, color: "text.secondary" } satisfies SxProps<Theme>
const RAW_SOURCE_SX = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: "monospace",
  fontSize: "0.8125rem",
  color: "var(--app-text-primary)",
} satisfies SxProps<Theme>

interface MarkdownErrorBoundaryProps { source: string, children: ReactNode }

/** Renders `children` with no wrapper element of its own: callers style markdown via `:first-child` /
 *  `:last-child` rules that must keep matching the markdown elements, not a wrapper node. */
export function MarkdownErrorBoundary({ source, children }: MarkdownErrorBoundaryProps) {
  // Function form so the fallback JSX is only built if an error occurred — this re-renders on every streamed chunk.
  const renderFallback = () => (
    <Box sx={FALLBACK_SX} data-testid="chatbox-markdown-error">
      <Typography variant="caption" sx={NOTICE_SX}>This content couldn&rsquo;t be formatted &mdash; showing the original text.</Typography>
      <Box sx={RAW_SOURCE_SX}>{source}</Box>
    </Box>
  )
  return <ErrorBoundary fallback={renderFallback}>{children}</ErrorBoundary>
}
